/**
 * Withdrawals Page
 * Universal withdrawal system for Drivers, PUDO Operators, and Sellers
 * With 0.75% processing fee and bank/Zelle options
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Wallet, DollarSign, Building2, CreditCard, ArrowUpRight, 
  Clock, CheckCircle, XCircle, Loader2, ArrowLeft, Settings,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Withdrawals = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [account, setAccount] = useState(null);
  
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [preferences, setPreferences] = useState({
    auto_withdraw_enabled: false,
    auto_withdraw_type: 'threshold',
    threshold_amount: null,
    scheduled_day: null,
    notify_on_auto_withdraw: true
  });
  
  const [accountForm, setAccountForm] = useState({
    payment_method: 'bank',
    bank_name: '',
    branch: '',
    account_type: 'savings',
    account_number: '',
    account_holder_name: '',
    zelle_email: '',
    zelle_phone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, historyRes, accountRes, prefsRes] = await Promise.all([
        fetch(`${API_URL}/api/withdrawals/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/withdrawals/history?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/withdrawals/account`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/withdrawals/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setBalance(data);
      }

      if (historyRes.ok) {
        const data = await historyRes.json();
        setWithdrawals(data.withdrawals || []);
      }

      if (accountRes.ok) {
        const data = await accountRes.json();
        setAccount(data.withdrawal_account);
        if (data.withdrawal_account) {
          setAccountForm(prev => ({
            ...prev,
            payment_method: data.withdrawal_account.payment_method || 'bank',
            ...data.withdrawal_account.bank_details,
            ...data.withdrawal_account.zelle_details
          }));
        }
      }

      if (prefsRes.ok) {
        const data = await prefsRes.json();
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async () => {
    setProcessing(true);
    try {
      const payload = {
        payment_method: accountForm.payment_method,
        bank_details: accountForm.payment_method === 'bank' ? {
          bank_name: accountForm.bank_name,
          branch: accountForm.branch,
          account_type: accountForm.account_type,
          account_number: accountForm.account_number,
          account_holder_name: accountForm.account_holder_name
        } : null,
        zelle_details: accountForm.payment_method === 'zelle' ? {
          zelle_email: accountForm.zelle_email,
          zelle_phone: accountForm.zelle_phone
        } : null
      };

      const res = await fetch(`${API_URL}/api/withdrawals/account`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Withdrawal account updated!');
        setShowAccountModal(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update account');
      }
    } catch (error) {
      toast.error('Error updating account');
    } finally {
      setProcessing(false);
    }
  };

  const savePreferences = async () => {
    setProcessing(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      
      const res = await fetch(`${API_URL}/api/withdrawals/preferences`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(preferences)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Auto-withdrawal preferences saved!");
      } else {
        toast.error(data.error?.message || "Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/withdrawals/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount,
          payment_method: account?.payment_method || 'bank'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Withdrawal requested!');
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to request withdrawal');
      }
    } catch (error) {
      toast.error('Error requesting withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const calculateFee = (amount) => {
    const fee = amount * 0.0075;
    const net = amount - fee;
    return { fee: fee.toFixed(2), net: net.toFixed(2) };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="h-3 w-3 mr-1" /> Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-gray-400" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Withdrawals</h1>
              <p className="text-gray-400">Withdraw your earnings to bank or Zelle</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData} className="border-slate-600">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <p className="text-emerald-300 text-sm mb-1">Available Balance</p>
                <p className="text-4xl font-bold text-white">J${balance?.available_balance?.toLocaleString() || '0.00'}</p>
                {balance?.balance_breakdown && (
                  <div className="flex flex-wrap gap-3 mt-2">
                    {Object.entries(balance.balance_breakdown).map(([key, value]) => (
                      <Badge key={key} variant="outline" className="border-emerald-500/50 text-emerald-300">
                        {key.replace('_', ' ')}: J${value?.toLocaleString() || 0}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => {
                  if (!balance?.has_withdrawal_account) {
                    setShowAccountModal(true);
                  } else {
                    setShowWithdrawModal(true);
                  }
                }}
                disabled={balance?.withdrawable <= 0}
              >
                <ArrowUpRight className="h-4 w-4 mr-2" /> Withdraw
              </Button>
            </div>
            {balance?.pending_withdrawal > 0 && (
              <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-400" />
                <span className="text-yellow-300">Pending withdrawal: J${balance.pending_withdrawal.toLocaleString()}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fee Info */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-gray-300">Processing Fee: <span className="text-white font-medium">0.75%</span></p>
              <p className="text-gray-500 text-sm">A small fee is deducted for bank charges and processing</p>
            </div>
          </CardContent>
        </Card>

        {/* Withdrawal Account */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Withdrawal Account</CardTitle>
              <CardDescription>Where your funds will be sent</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAccountModal(true)}>
              <Settings className="h-4 w-4 mr-2" /> {account?.payment_method ? 'Edit' : 'Setup'}
            </Button>
          </CardHeader>
          <CardContent>
            {account?.payment_method ? (
              <div className="flex items-center gap-4">
                {account.payment_method === 'bank' ? (
                  <>
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <Building2 className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{account.bank_details?.bank_name}</p>
                      <p className="text-gray-400 text-sm">
                        {account.bank_details?.branch} • {account.bank_details?.account_type} • 
                        ****{account.bank_details?.account_number?.slice(-4)}
                      </p>
                      <p className="text-gray-500 text-sm">{account.bank_details?.account_holder_name}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <CreditCard className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Zelle</p>
                      <p className="text-gray-400 text-sm">
                        {account.zelle_details?.zelle_email || account.zelle_details?.zelle_phone}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No withdrawal account set up. Click Setup to add your bank or Zelle details.</p>
            )}
          </CardContent>
        </Card>

        {/* Auto-Withdrawal Preferences */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Auto-Withdrawal Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Enable Auto-Withdrawal</Label>
                <p className="text-sm text-gray-400">Automatically withdraw when conditions are met</p>
              </div>
              <Switch
                checked={preferences.auto_withdraw_enabled}
                onCheckedChange={(checked) => setPreferences({...preferences, auto_withdraw_enabled: checked})}
              />
            </div>
            
            {preferences.auto_withdraw_enabled && (
              <>
                <div className="space-y-2">
                  <Label className="text-white">Withdrawal Type</Label>
                  <Select 
                    value={preferences.auto_withdraw_type}
                    onValueChange={(v) => setPreferences({...preferences, auto_withdraw_type: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="threshold">When balance reaches threshold</SelectItem>
                      <SelectItem value="scheduled">On scheduled day each month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {preferences.auto_withdraw_type === "threshold" && (
                  <div className="space-y-2">
                    <Label className="text-white">Threshold Amount (J$)</Label>
                    <Input
                      type="number"
                      min="500"
                      placeholder="e.g., 5000"
                      value={preferences.threshold_amount}
                      onChange={(e) => setPreferences({...preferences, threshold_amount: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    <p className="text-xs text-gray-400">Auto-withdraw when balance exceeds this amount (min J$500)</p>
                  </div>
                )}
                
                {preferences.auto_withdraw_type === "scheduled" && (
                  <div className="space-y-2">
                    <Label className="text-white">Day of Month</Label>
                    <Select 
                      value={preferences.scheduled_day?.toString()}
                      onValueChange={(v) => setPreferences({...preferences, scheduled_day: parseInt(v)})}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,5,10,15,20,25,28].map(day => (
                          <SelectItem key={day} value={day.toString()}>{day}th of each month</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Notify on Auto-Withdrawal</Label>
                    <p className="text-sm text-gray-400">Receive notification when auto-withdrawal occurs</p>
                  </div>
                  <Switch
                    checked={preferences.notify_on_auto_withdraw}
                    onCheckedChange={(checked) => setPreferences({...preferences, notify_on_auto_withdraw: checked})}
                  />
                </div>
                
                <Button 
                  onClick={savePreferences}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                  disabled={processing}
                >
                  {processing ? "Saving..." : "Save Preferences"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Withdrawal History */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Withdrawal History</CardTitle>
          </CardHeader>
          <CardContent>
            {withdrawals.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No withdrawals yet</p>
            ) : (
              <div className="space-y-3">
                {withdrawals.map((w, idx) => (
                  <div key={w.id || idx} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                    <div>
                      <p className="text-white font-medium">J${w.net_amount?.toLocaleString()}</p>
                      <p className="text-gray-500 text-sm">
                        Requested: J${w.requested_amount?.toLocaleString()} • Fee: J${w.processing_fee?.toLocaleString()}
                      </p>
                      <p className="text-gray-600 text-xs">{new Date(w.created_at).toLocaleDateString()}</p>
                    </div>
                    {getStatusBadge(w.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Account Setup Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Withdrawal Account</DialogTitle>
            <DialogDescription>Choose how you want to receive your funds</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Payment Method</Label>
              <Select 
                value={accountForm.payment_method} 
                onValueChange={(v) => setAccountForm(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {accountForm.payment_method === 'bank' ? (
              <>
                <div>
                  <Label className="text-gray-300">Bank Name</Label>
                  <Input
                    value={accountForm.bank_name}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="e.g., NCB, Scotiabank"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Branch</Label>
                  <Input
                    value={accountForm.branch}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, branch: e.target.value }))}
                    placeholder="e.g., Half Way Tree"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Account Type</Label>
                  <Select 
                    value={accountForm.account_type} 
                    onValueChange={(v) => setAccountForm(prev => ({ ...prev, account_type: v }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="savings">Savings</SelectItem>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="current">Current</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Account Number</Label>
                  <Input
                    value={accountForm.account_number}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="Enter account number"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Account Holder Name</Label>
                  <Input
                    value={accountForm.account_holder_name}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                    placeholder="Name on account"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-gray-300">Zelle Email</Label>
                  <Input
                    type="email"
                    value={accountForm.zelle_email}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, zelle_email: e.target.value }))}
                    placeholder="email@example.com"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Or Zelle Phone</Label>
                  <Input
                    value={accountForm.zelle_phone}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, zelle_phone: e.target.value }))}
                    placeholder="+1 xxx xxx xxxx"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
              </>
            )}

            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600" 
              onClick={handleAccountSubmit}
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Request Withdrawal</DialogTitle>
            <DialogDescription>Enter the amount you want to withdraw</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Amount (JMD)</Label>
              <Input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-slate-700 border-slate-600 text-white mt-1 text-lg"
              />
              <p className="text-gray-500 text-sm mt-1">
                Available: J${balance?.withdrawable?.toLocaleString() || 0} • Min: J$500
              </p>
            </div>

            {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
              <div className="p-4 bg-slate-700/50 rounded-lg space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Amount</span>
                  <span>J${parseFloat(withdrawAmount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Processing Fee (0.75%)</span>
                  <span>-J${calculateFee(parseFloat(withdrawAmount)).fee}</span>
                </div>
                <div className="border-t border-slate-600 pt-2 flex justify-between text-white font-medium">
                  <span>You'll Receive</span>
                  <span className="text-emerald-400">J${calculateFee(parseFloat(withdrawAmount)).net}</span>
                </div>
              </div>
            )}

            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600" 
              onClick={handleWithdraw}
              disabled={processing || !withdrawAmount || parseFloat(withdrawAmount) < 500}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Request Withdrawal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Withdrawals;
